import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FormRow {
  readonly formId: string;
  readonly name: string;
}

interface FieldRow {
  readonly fieldId: string;
  readonly label: string;
  readonly fieldType: string;
  readonly isRequired: boolean;
}

/**
 * GR L28.04 — App Preview (`/lcnc/apps/{id}/preview`). Renders the application's
 * primary `lcnc_forms` form as an end-user would see it — a read-only render of
 * each `lcnc_form_fields` control — so the builder can sanity-check the layout
 * before publishing.
 */
export function GrAppPreviewPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [formName, setFormName] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const formsRes = await apiRequest<{ data: FormRow[] }>(`/v1/lcnc/forms?applicationId=${id}`, { token });
        const first = formsRes.data[0];
        if (!first) { if (alive) { setEmpty(true); setError(null); } return; }
        const fieldsRes = await apiRequest<{ data: FieldRow[] }>(`/v1/lcnc/forms/${first.formId}/fields`, { token });
        if (alive) { setFormName(first.name); setFields(fieldsRes.data); setEmpty(false); setError(null); }
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, id]);

  return (
    <main aria-label="lcnc-preview">
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr28.preview.loading')}</p> : error ? null : empty ? (
        <p aria-label="empty">{t('gr28.preview.empty')}</p>
      ) : (
        <>
          <h1 aria-label="preview-form-name">{formName}</h1>
          <p aria-label="field-count">{t('gr28.preview.count', { count: String(fields.length) })}</p>
          <form aria-label="preview-fields">
            {fields.map((f) => (
              <label key={f.fieldId}>
                {f.label}{f.isRequired ? ' *' : ''}
                <input aria-label={`preview-${f.fieldId}`} type="text" disabled placeholder={f.fieldType} />
              </label>
            ))}
          </form>
        </>
      )}
    </main>
  );
}
